import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { StatusCode } from 'ha/modules/StatusCode';
import { OTHER } from 'ha/constants/pageNames';
import { onPageMount } from 'ha/modules/Analytics/actions';
import { FontInfo } from 'ha/components/LoadFonts';
import {
  getCurrentLanguage,
  getIsAuthenticated,
} from 'ha/modules/AuthLogic/selectors';
import { getFromStorage, removeFromStorage } from 'ha/utils/sessionStorage';
import { trigger as triggerHotjar } from 'ha/modules/Hotjar/actions';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { useServices } from 'ha/services';

import { getShowNewTaxonomy } from 'ha/modules/ULE/selectors';
import { PageLayout } from '../components/PageLayout';
import { PageProvider } from '../contexts/PageContext';

export interface Props {
  noFollowFooter?: boolean;
  pageBanner?: React.ReactNode;
  globalBanner?: React.ReactNode;
  category?: string;
  hasFooter?: boolean;
  hasHeader?: boolean;
  name: string;
  remarketingTag?: object;
  statusCode?: number;
  traits?: object;
  hideIntercom?: boolean;
  activeFonts?: FontInfo[];
  fixedFooterContent?: React.ReactNode;
  showBottomNav?: boolean;
  showMiddleMenu?: boolean;
  showSearchBar?: boolean;
  searchBarElement?: React.ReactNode;
  showWeWantToPay?: boolean;
  showLogo?: boolean;
  hasSidebar?: boolean;
  hasStickyNav?: boolean;
  hasProgressBar?: boolean;
  advertiserLogo?: string;
  showActions?: boolean;
  showMobileDrawerMenu?: boolean;
  flexContent?: boolean;
  showUkraineFlair?: boolean;
}

export const Page: React.FC<Props> = ({
  activeFonts,
  category = OTHER,
  children,
  fixedFooterContent,
  globalBanner,
  hasFooter = true,
  hasHeader = true,
  hideIntercom,
  name,
  noFollowFooter = true,
  pageBanner,
  remarketingTag,
  showBottomNav,
  showMiddleMenu = true,
  showSearchBar,
  searchBarElement,
  showWeWantToPay = false,
  showLogo = true,
  hasStickyNav,
  statusCode = 200,
  traits = {},
  hasProgressBar,
  hasSidebar,
  advertiserLogo,
  showMobileDrawerMenu = true,
  showActions = true,
  flexContent = false,
  showUkraineFlair = false,
}) => {
  const location = useLocation();
  const { lg, md, xl } = useMediaQuery();

  const isAuthenticated = useSelector(getIsAuthenticated);
  const showNewTaxonomy = useSelector(getShowNewTaxonomy);

  const allowSidebar = React.useMemo(() => {
    return (md || lg || xl) && showNewTaxonomy;
  }, [md, lg, xl, showNewTaxonomy]);

  const language = useSelector(getCurrentLanguage);
  const dispatch = useDispatch();
  const services = useServices();

  React.useEffect(() => {
    services.analytics.init({ withIntercom: !hideIntercom });
  }, [services.analytics]);

  React.useEffect(() => {
    if (!process.browser) {
      return;
    }

    const eventHandler = () => {
      dispatch(
        onPageMount({
          category,
          name,
          traits: { ...traits, language },
          remarketingTag,
          hideIntercom: hideIntercom || !lg,
        }),
      );
    };

    // fix analytics issue with back navigation reporting incorrect URL
    setTimeout(eventHandler, 200);
  }, []);

  React.useEffect(() => {
    if (location.pathname.includes('my/book/contact')) return;

    const shouldShowHotjarPoll = getFromStorage('unregisteredBookingFlow');
    if (shouldShowHotjarPoll) {
      dispatch(triggerHotjar('unregistered_bookingflow_dropoff_poll'));
      removeFromStorage('unregisteredBookingFlow');
    }
  }, []);

  return (
    <PageProvider name={name} category={category}>
      <StatusCode status={statusCode} />
      <PageLayout
        activeFonts={activeFonts}
        fixedFooterContent={fixedFooterContent}
        globalBanner={globalBanner}
        hasFooter={hasFooter}
        hasHeader={hasHeader}
        hasSidebar={allowSidebar && hasSidebar}
        showDashboardCTA={allowSidebar && !hasSidebar}
        hasProgressBar={hasProgressBar}
        hasStickyNav={hasStickyNav}
        isAuthenticated={isAuthenticated}
        noFollowFooter={noFollowFooter}
        pageBanner={pageBanner}
        showBottomNav={showBottomNav}
        showMiddleMenu={showMiddleMenu}
        showSearchBar={showSearchBar}
        searchBarElement={searchBarElement}
        showWeWantToPay={showWeWantToPay}
        showActions={showActions}
        showLogo={showLogo}
        showMobileDrawerMenu={showMobileDrawerMenu}
        advertiserLogo={advertiserLogo}
        flexContent={flexContent}
        showUkraineFlair={showUkraineFlair}
      >
        {children}
      </PageLayout>
    </PageProvider>
  );
};
